/**
 * QuestionCommentsSection - Colapsível "Comentários" nas telas de resolver questão.
 *
 * Comentários sociais/compartilhados por questão, em seção aberta para não
 * comprimir a largura das threads. Reusa CommentThread da Comunidade e mapeia
 * o tema global para o shape de `colors` que a thread/composer esperam.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
    View,
    TextInput,
    TouchableOpacity,
    ActivityIndicator,
    StyleSheet,
    Keyboard,
} from 'react-native';
// `Alert` daqui, nao do react-native: no navegador o Alert do RN e um stub
// vazio e engole tudo em silencio (ver src/utils/appAlert.js). Em nativo e
// reexportacao direta - comportamento do app inalterado.
import { Alert } from '../../utils/appAlert';
import { Text } from '../ui/AppText';
import { useTheme } from '../../contexts/ThemeContext';
import CommentThread from '../Community/PostDetailModal/CommentThread';
import {
    useQuestionComments,
    useAddQuestionComment,
} from '../../hooks/queries/useQuestionCommentsQuery';

const QuestionCommentsSection = ({ questionId }) => {
    const { theme } = useTheme();
    const [expanded, setExpanded] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [replyingTo, setReplyingTo] = useState(null);

    // Só busca quando o usuário abre o colapsível (economiza round-trip).
    const { data: comments = [], isLoading } = useQuestionComments(questionId, expanded);
    const addComment = useAddQuestionComment();

    // Mapeia o tema global para o shape esperado por CommentThread/composer.
    const colors = useMemo(() => ({
        bgTertiary: theme.colors.surfaceSecondary,
        bgInput: theme.colors.inputBackground,
        border: theme.colors.border,
        textPrimary: theme.colors.textPrimary,
        textSecondary: theme.colors.textSecondary,
        textTertiary: theme.colors.textTertiary,
        accentPrimary: theme.colors.primary,
    }), [theme]);

    const totalCount = useMemo(() => {
        const countTree = (nodes) =>
            nodes.reduce((acc, n) => acc + 1 + countTree(n.replies || []), 0);
        return countTree(comments);
    }, [comments]);

    const handleReplyTarget = useCallback((target) => {
        setReplyingTo(target);
    }, []);

    const handleClearReplyingTo = useCallback(() => {
        setReplyingTo(null);
    }, []);

    const handleSend = useCallback(async () => {
        const content = commentText.trim();
        if (!content || addComment.isPending) return;

        const parentId = replyingTo?.id || null;
        let result;
        try {
            result = await addComment.mutateAsync({ questionId, content, parentId });
        } catch (error) {
            Alert.alert('Erro', error?.message || 'Não foi possível enviar o comentário.');
            return;
        }

        if (!result?.success) {
            Alert.alert('Erro', result?.error || 'Não foi possível enviar o comentário.');
            return;
        }

        setCommentText('');
        setReplyingTo(null);
        Keyboard.dismiss();
    }, [commentText, replyingTo, questionId, addComment]);

    return (
        <View style={[styles.section, { borderTopColor: colors.border }]}>
            <TouchableOpacity
                style={styles.header}
                onPress={() => setExpanded(prev => !prev)}
                activeOpacity={0.7}
            >
                <View style={styles.headerTitleWrap}>
                    <Text style={[styles.headerTitle, { color: colors.textPrimary }]}>
                        Comentários
                    </Text>
                    <Text style={[styles.headerSubtitle, { color: colors.textTertiary }]}>
                        Discussão da questão
                    </Text>
                </View>
                {totalCount > 0 && (
                    <View style={[styles.countBadge, { backgroundColor: colors.bgTertiary }]}>
                        <Text style={[styles.countText, { color: colors.accentPrimary }]}>
                            {totalCount}
                        </Text>
                    </View>
                )}
                <Text style={[styles.expandIcon, { color: colors.textTertiary }]}>
                    {expanded ? '▲' : '▼'}
                </Text>
            </TouchableOpacity>

            {expanded && (
                <View style={styles.body}>
                    {isLoading ? (
                        <ActivityIndicator color={colors.accentPrimary} style={styles.loader} />
                    ) : comments.length === 0 ? (
                        <Text style={[styles.emptyText, { color: colors.textTertiary }]}>
                            Nenhum comentário ainda. Seja o primeiro!
                        </Text>
                    ) : (
                        comments.map((comment, index) => (
                            <CommentThread
                                key={`qcomment-${comment.id}`}
                                comment={comment}
                                depth={0}
                                isLast={index === comments.length - 1}
                                colors={colors}
                                onReply={handleReplyTarget}
                            />
                        ))
                    )}

                    {replyingTo && (
                        <View style={[styles.replyingTo, { borderLeftColor: colors.accentPrimary }]}>
                            <Text style={[styles.replyingToLabel, { color: colors.textTertiary }]}>
                                Respondendo a{' '}
                                <Text style={{ color: colors.accentPrimary }}>@{replyingTo.author}</Text>
                            </Text>
                            <TouchableOpacity onPress={handleClearReplyingTo} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                                <Text style={[styles.replyingToClose, { color: colors.textTertiary }]}>✕</Text>
                            </TouchableOpacity>
                        </View>
                    )}

                    <View style={[styles.composer, { borderTopColor: colors.border }]}>
                        <TextInput
                            style={[styles.input, {
                                backgroundColor: colors.bgInput,
                                color: colors.textPrimary,
                                borderColor: colors.border,
                            }]}
                            placeholder={replyingTo ? `Responder @${replyingTo.author}...` : 'Escreva um comentário...'}
                            placeholderTextColor={colors.textTertiary}
                            value={commentText}
                            onChangeText={setCommentText}
                            multiline
                        />
                        <TouchableOpacity
                            style={[styles.sendBtn, {
                                backgroundColor: commentText.trim() ? colors.accentPrimary : colors.bgTertiary,
                            }]}
                            onPress={handleSend}
                            disabled={!commentText.trim() || addComment.isPending}
                        >
                            {addComment.isPending ? (
                                <ActivityIndicator color="#FFFFFF" size="small" />
                            ) : (
                                <Text style={styles.sendBtnText}>➤</Text>
                            )}
                        </TouchableOpacity>
                    </View>
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    section: {
        marginTop: 18,
        paddingTop: 14,
        borderTopWidth: 1,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 6,
    },
    headerTitleWrap: {
        flex: 1,
    },
    headerTitle: {
        fontSize: 17,
        fontWeight: '700',
    },
    headerSubtitle: {
        fontSize: 12,
        marginTop: 2,
    },
    countBadge: {
        minWidth: 28,
        height: 24,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 8,
        marginRight: 10,
    },
    countText: {
        fontSize: 12,
        fontWeight: '700',
    },
    expandIcon: {
        fontSize: 11,
        width: 18,
        textAlign: 'right',
    },
    body: {
        paddingTop: 10,
    },
    loader: {
        marginVertical: 16,
    },
    emptyText: {
        fontSize: 13,
        textAlign: 'center',
        paddingVertical: 18,
    },
    replyingTo: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderLeftWidth: 3,
        paddingLeft: 10,
        paddingVertical: 6,
        marginTop: 10,
    },
    replyingToLabel: {
        fontSize: 12,
        flex: 1,
    },
    replyingToClose: {
        fontSize: 14,
        fontWeight: '700',
        paddingLeft: 8,
    },
    composer: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 1,
    },
    input: {
        flex: 1,
        minHeight: 44,
        maxHeight: 120,
        borderRadius: 10,
        borderWidth: 1,
        paddingHorizontal: 12,
        paddingTop: 11,
        paddingBottom: 11,
        fontSize: 14,
    },
    sendBtn: {
        width: 44,
        height: 44,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: 8,
    },
    sendBtnText: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: '700',
    },
});

export default QuestionCommentsSection;
